import React, { useState, useEffect } from 'react';
import { Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import api from '../api';

const SESSION_STATUS_COLOR = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' };
const SESSION_STATUS_LABEL = { draft: 'Draft', active: 'Active', completed: 'Completed', canceled: 'Canceled' };

/**
 * An agent's completed error history around one submission: a section-by-month
 * pivot, then a flat row per submission-section.
 *
 * Lifted out of ErrorSubmissionPage so the coaching session page can show the
 * same thing in a modal from its Linked Errors table. It owns its own fetching,
 * so a caller only has to say which submission to anchor on.
 *
 * `isCompleted` decides whether the anchor submission counts toward its own
 * history: a completed one is included, an in-flight one is excluded via
 * exclude_id so a score still being worked on does not colour its own context.
 */
export default function ErrorQuestionHistory({
  agentUserId,
  agentName,
  anchorDate,
  submissionId,
  isCompleted,
}) {
  const [qStatsRows, setQStatsRows]         = useState([]);
  const [qStatsLoading, setQStatsLoading]   = useState(false);
  const [flatSectionRows, setFlatRows]      = useState([]);
  const [flatLoading, setFlatLoading]       = useState(false);

  const range = agentUserId
    ? { start: dayjs(anchorDate || undefined).subtract(3, 'month').startOf('month'), end: dayjs() }
    : null;
  const startStr = range ? range.start.format('YYYY-MM-DD') : null;
  const endStr   = range ? range.end.format('YYYY-MM-DD')   : null;

  useEffect(() => {
    if (!agentUserId || !startStr || !endStr) return;
    const base = {
      agent_user_id: agentUserId,
      start_date:    startStr,
      end_date:      endStr,
      ...(isCompleted ? {} : (submissionId ? { exclude_id: submissionId } : {})),
    };
    setQStatsLoading(true);
    api.get('/api/errors/question-stats', { params: { ...base, group_by: 'month' } })
      .then((r) => setQStatsRows(r.data))
      .catch(() => setQStatsRows([]))
      .finally(() => setQStatsLoading(false));
    setFlatLoading(true);
    api.get('/api/errors/question-stats', { params: { ...base, group_by: 'section_date' } })
      .then((r) => setFlatRows(r.data))
      .catch(() => setFlatRows([]))
      .finally(() => setFlatLoading(false));
  }, [agentUserId, startStr, endStr, submissionId, isCompleted]);

  const months  = [...new Set(qStatsRows.map((r) => r.MonthKey))].sort();
  const mLabels = {};
  months.forEach((mk) => { mLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });

  // Section totals per month, summed across that section's questions.
  const secByKey = {};
  const secOrder = [];
  qStatsRows.forEach((r) => {
    if (!secByKey[r.SectionName]) {
      secByKey[r.SectionName] = { _key: r.SectionName, SectionName: r.SectionName };
      secOrder.push(r.SectionName);
    }
    const prev = secByKey[r.SectionName][r.MonthKey] || { errors: 0, feedbacks: 0 };
    secByKey[r.SectionName][r.MonthKey] = {
      errors:    prev.errors    + (r.TotalErrors    || 0),
      feedbacks: prev.feedbacks + (r.TotalFeedbacks || 0),
    };
  });
  const secPivotData = secOrder.map((k) => secByKey[k]);

  const secCols = [
    { title: 'Section', dataIndex: 'SectionName', key: '__sec', ellipsis: true },
    ...months.map((mk) => ({
      title: mLabels[mk] || mk, key: mk, width: 120, align: 'center',
      render: (_, r) => {
        const cell = r[mk];
        if (!cell || (cell.errors === 0 && cell.feedbacks === 0)) return <span style={{ color: '#d9d9d9' }}>—</span>;
        return (
          <div>
            {cell.errors > 0 && <Tag color="red" style={{ margin: 0 }}>{cell.errors} {cell.errors === 1 ? 'error' : 'errors'}</Tag>}
            {cell.feedbacks > 0 && <Tag color="orange" style={{ margin: '2px 0 0' }}>{cell.feedbacks} {cell.feedbacks === 1 ? 'feedback' : 'feedbacks'}</Tag>}
          </div>
        );
      },
    })),
  ];

  const flatCols = [
    {
      title: 'Submission ID', dataIndex: 'SubmissionID', key: 'SubmissionID', width: 120, align: 'right',
      // window.open, not <a target="_blank">: Chrome clones sessionStorage into
      // a script-opened window but not into one opened by an anchor. MSAL keeps
      // the signed-in account there, so the anchor version landed on a fresh
      // sign-in.
      render: (v) => (
        <Typography.Link onClick={() => window.open(`/errors/${v}`, '_blank')}>{v}</Typography.Link>
      ),
    },
    { title: 'Section',       dataIndex: 'SectionName', key: 'SectionName', width: 200, ellipsis: true },
    { title: 'Date of Error', dataIndex: 'DateOfError', key: 'DateOfError', width: 120 },
    { title: 'Created Date',  dataIndex: 'CreateDate',  key: 'CreateDate',  width: 120 },
    {
      // Coaching sessions raised off that submission, canceled ones excluded by
      // the API.
      title: 'Coachings', dataIndex: 'Coachings', key: 'Coachings', width: 260,
      render: (list) => !list?.length
        ? <Typography.Text type="secondary">-</Typography.Text>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {list.map((c) => (
              <div key={c.ID} style={{ fontSize: 12, lineHeight: 1.4 }}>
                <Typography.Link onClick={() => window.open(`/coaching/sessions/${c.ID}`, '_blank')}>
                  #{c.ID}
                </Typography.Link>
                {c.CreatedOn && <span style={{ color: '#8c8c8c' }}> · {c.CreatedOn}</span>}
                {c.RequestType && <span style={{ color: '#8c8c8c' }}> · {c.RequestType}</span>}
                {c.Linked != null && <span style={{ color: '#8c8c8c' }}> · {c.Linked ? 'Linked Sections' : 'All Sections'}</span>}
                <Tag color={SESSION_STATUS_COLOR[c.Status] ?? 'default'} style={{ marginLeft: 6 }}>
                  {SESSION_STATUS_LABEL[c.Status] ?? c.Status}
                </Tag>
              </div>
            ))}
          </div>
        ),
    },
    {
      title: 'Errors', dataIndex: 'TotalErrors', key: 'TotalErrors', width: 90, align: 'center',
      render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <span style={{ color: '#d9d9d9' }}>—</span>,
    },
    {
      title: 'Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 100, align: 'center',
      render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: '#d9d9d9' }}>—</span>,
    },
  ];

  return (
    <>
      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
        Completed errors for {agentName}
        {range && ` · ${range.start.format('MMM D, YYYY')} — ${range.end.format('MMM D, YYYY')}`}
        {' '}{isCompleted ? '(including this submission)' : '(excluding this submission)'}
      </Typography.Text>
      <Table
        rowKey="_key"
        dataSource={secPivotData}
        columns={secCols}
        loading={qStatsLoading}
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
        bordered
        style={{ marginBottom: 16 }}
        locale={{ emptyText: 'No completed error submissions in this date range.' }}
      />
      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
        Section History by Submission
      </Typography.Text>
      <Table
        rowKey={(r) => `${r.SubmissionID}||${r.SectionName}`}
        dataSource={flatSectionRows}
        loading={flatLoading}
        size="small"
        pagination={false}
        bordered
        scroll={{ x: 'max-content' }}
        style={{ marginBottom: 16 }}
        locale={{ emptyText: 'No completed error submissions in this date range.' }}
        columns={flatCols}
      />
    </>
  );
}
